import pptxgen from "pptxgenjs";

export type SimpleClassificationRow = {
    label: string;
    value: string;
};

export type EquipmentClassification = {
    top: {
        label: string;
        value: string;
    }[];

    bottom: {
        leftLabel: string;
        leftValue: string;
        rightLabel: string;
        rightValue: string;
    }[];
};

interface IncidentData {
    incidentNumber: string;
    date: string;
    incidentType: string;
    incidentUrl: string;

    location: string;
    department: string;
    area: string;
    initiator: string;
    description: string;

    rootCauses: {
        description: string;
        ishikawaCategory: string;
    }[];

    photos?: {
        path: string;
    }[];

    actions: {
        description: string;
        deadline: string;
    }[];
    blockClassificationTitle: string;
    blockClassificationRows: SimpleClassificationRow[] | EquipmentClassification;
    blockClassificationType: "simple" | "equipment";
}

export const createIncidentPresentation = (
    data: IncidentData
): pptxgen => {
    const pptx = new pptxgen();

    // =========================================================
    // НАСТРОЙКИ
    // =========================================================

    pptx.layout = "LAYOUT_WIDE";

    pptx.author = "Supply Portal";
    pptx.company = "Supply Portal";
    pptx.subject = "Инцидент по безопасности";
    pptx.title = `Инцидент ${data.incidentNumber}`;

    pptx.theme = {
        headFontFace: "Arial",
        bodyFontFace: "Arial",
        lang: "ru-RU",
    };

    const slide = pptx.addSlide();

    slide.background = {
        color: "FFFFFF",
    };

    // =========================================================
    // ЦВЕТА
    // =========================================================

    const COLORS = {
        header: "004D6D",
        section: "006B9F",
        border: "006B9F",
        text: "222222",
        white: "FFFFFF",
        link: "0070C0",
    };

    const FONT = "Arial";

    // =========================================================
    // РАЗМЕРЫ
    // =========================================================

    const SLIDE_W = 13.333;
    const SLIDE_H = 7.5;

    const HEADER_H = 0.40;
    const SECTION_H = 0.40;

    const LEFT_W = 6.06;
    const RIGHT_W = SLIDE_W - LEFT_W;

    const LABEL_W = 2.42;

    const ROW_H = 0.405;

    // =========================================================
    // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
    // =========================================================

    const addRect = (
        x: number,
        y: number,
        w: number,
        h: number,
        fill = COLORS.white,
        line = COLORS.border,
        lineWidth = 0.6
    ) => {
        slide.addShape(
            pptx.ShapeType.rect,
            {
                x,
                y,
                w,
                h,

                line: {
                    color: line,
                    width: lineWidth,
                },

                fill: {
                    color: fill,
                },
            }
        );
    };

    const addText = (
        text: string,
        x: number,
        y: number,
        w: number,
        h: number,
        options: {
            fontSize?: number;
            bold?: boolean;
            color?: string;
            align?: "left" | "center" | "right";
            valign?: "top" | "mid" | "bottom";
            margin?: number;
            underline?: boolean;
            hyperlink?: any;
            // Индивидуальные отступы
            paddingX?: number;
            paddingY?: number;
        } = {}
    ) => {
        const normalizeText = (value: any): string => {
            if (value == null) {
                return "-";
            }

            if (typeof value === "string" || typeof value === "number") {
                return String(value);
            }

            if (Array.isArray(value)) {
                return value.join(", ");
            }

            if (typeof value === "object") {
                if ("name" in value) {
                    return String(value.name);
                }

                if ("title" in value) {
                    return String(value.title);
                }

                if ("label" in value) {
                    return String(value.label);
                }
            }

            return String(value);
        };
        // Реальный визуальный отступ от границы ячейки
        const PADDING_X = 0.08;

        const PADDING_Y = 0.04;
        // console.log(normalizeText(text));
        slide.addText(
            normalizeText(text) || "-",
            {
                // Уменьшаем сам текстовый блок
                x: x + PADDING_X,
                y: y + PADDING_Y,

                w: Math.max(
                    w - PADDING_X * 2,
                    0.1
                ),

                h: Math.max(
                    h - PADDING_Y * 2,
                    0.1
                ),

                fontFace: FONT,

                fontSize:
                    options.fontSize ?? 8.5,

                bold:
                    options.bold ?? false,

                color:
                    options.color ?? COLORS.text,

                margin: 0,

                align:
                    options.align ?? "left",

                valign:
                    options.valign ?? "mid",

                underline:
                    options.underline ?? false,

                breakLine: false,

                fit: "shrink",
                hyperlink: options.hyperlink || false,

                // Важно для переноса длинного текста
                // внутри ячейки
                paraSpaceAfterPt: 0,
            }
        );
    };

    const addSectionHeader = (
        text: string,
        x: number,
        y: number,
        w: number
    ) => {
        addRect(
            x,
            y,
            w,
            SECTION_H,
            COLORS.section,
            COLORS.section,
            0
        );

        addText(
            text,
            x,
            y,
            w,
            SECTION_H,
            {
                fontSize: 9,
                color: COLORS.white,
                align: "center",
                valign: "mid",
                margin: 0,
            }
        );
    };

    const addCell = (
        text: string,
        x: number,
        y: number,
        w: number,
        h: number,
        options: {
            fontSize?: number;
            bold?: boolean;
            align?: "left" | "center" | "right";
            valign?: "top" | "mid" | "bottom";
            margin?: number;
            paddingX?: number;
            paddingY?: number;
        } = {}
    ) => {
        addRect(
            x,
            y,
            w,
            h,
            COLORS.white,
            COLORS.border,
            0.6
        );

        addText(
            text || "-",
            x,
            y,
            w,
            h,
            {
                fontSize:
                    options.fontSize ?? 8.5,

                bold:
                    options.bold ?? false,

                align:
                    options.align ?? "left",

                valign:
                    options.valign ?? "mid",
            }
        );
    };

    const getRowHeightByText = (
        text: string,
        width: number
    ): number => {
        // Настройки текста, соответствующие генератору
        const FONT_SIZE = 8;
        const LINE_HEIGHT = 1.2;

        // Минимальная высота строки
        const MIN_ROW_HEIGHT = 0.36;

        // Вертикальный запас сверху + снизу
        const VERTICAL_PADDING = 0.04;

        // Примерная средняя ширина символа
        // относительно размера шрифта
        const AVG_CHAR_WIDTH = 0.52;

        if (!text) {
            return MIN_ROW_HEIGHT;
        }

        // Горизонтальные отступы текста внутри ячейки
        const HORIZONTAL_PADDING = 0.16;

        // Реальная ширина,
        // доступная непосредственно под текст
        const textWidth = Math.max(
            width - HORIZONTAL_PADDING,
            0.1
        );

        // Примерное количество символов,
        // которое помещается в одну строку
        const charsPerLine = Math.max(
            Math.floor(
                (textWidth * 72) /
                (FONT_SIZE * AVG_CHAR_WIDTH)
            ),
            1
        );

        // Учитываем переносы строк
        const lines = text
            .split("\n")
            .reduce(
                (totalLines, line) => {
                    return (
                        totalLines +
                        Math.max(
                            1,
                            Math.ceil(
                                line.length /
                                charsPerLine
                            )
                        )
                    );
                },
                0
            );

        // Высота одной строки текста
        // переводим pt в дюймы
        const lineHeight =
            (FONT_SIZE * LINE_HEIGHT) / 72;

        // Рассчитываем итоговую высоту
        const calculatedHeight =
            lines * lineHeight +
            VERTICAL_PADDING;

        return Math.max(
            calculatedHeight,
            MIN_ROW_HEIGHT
        );
    };

    // =========================================================
    // HEADER
    // =========================================================

    addRect(
        0,
        0,
        SLIDE_W,
        HEADER_H,
        COLORS.header,
        COLORS.header,
        0
    );

    // Номер
    addText(
        data.incidentNumber,
        0.70,
        0.07,
        1.30,
        0.25,
        {
            fontFace: FONT,
            fontSize: 9,

            color: COLORS.link,
            underline: {
                color: COLORS.link,
            },

            margin: 0,
            align: "left",
            valign: "mid",

            hyperlink: {
                url: data.incidentUrl,
            },
        }
    );

    // Дата
    addText(
        data.date,
        3.00,
        0.07,
        2.00,
        0.25,
        {
            fontSize: 9,
            color: COLORS.white,
            align: "center",
            margin: 0,
        }
    );

    // Тип инцидента
    //
    // ВАЖНО:
    // теперь он центрируется относительно правого блока,
    // то есть находится строго над "Описание"
    addText(
        data.incidentType,
        LEFT_W,
        0.07,
        RIGHT_W,
        0.25,
        {
            fontSize: 9,
            color: COLORS.white,
            align: "center",
            margin: 0,
        }
    );

    // =========================================================
    // ЛОКАЦИЯ / ОПИСАНИЕ
    // =========================================================

    const MAIN_Y = HEADER_H;

    addSectionHeader(
        "Локация",
        0,
        MAIN_Y,
        LEFT_W
    );

    addSectionHeader(
        "Описание",
        LEFT_W,
        MAIN_Y,
        RIGHT_W
    );

    const INFO_Y =
        MAIN_Y + SECTION_H;

    const infoRows = [
        {
            label: "Локация:",
            value: data.location,
        },
        {
            label: "Отдел:",
            value: data.department,
        },
        {
            label: "Участок:",
            value: data.area,
        },
        {
            label: "ФИО инициатора:",
            value: data.initiator,
        },
    ];

    infoRows.forEach(
        (row, index) => {
            const y =
                INFO_Y +
                index * ROW_H;

            addCell(
                row.label,
                0,
                y,
                LABEL_W,
                ROW_H,
                {
                    fontSize: 8.5,
                    bold: true,
                }
            );

            addCell(
                row.value,
                LABEL_W,
                y,
                LEFT_W - LABEL_W,
                ROW_H,
                {
                    fontSize: 8.5,
                }
            );
        }
    );

    addCell(
        data.description,
        LEFT_W,
        INFO_Y,
        RIGHT_W,
        ROW_H * infoRows.length,
        {
            fontSize: 8.5,
            valign: "top",
        }
    );

    // =========================================================
    // КЛАССИФИКАЦИЯ / КОРНЕВЫЕ ПРИЧИНЫ
    // =========================================================

    const CLASS_Y =
        INFO_Y +
        ROW_H *
        infoRows.length;

    addSectionHeader(
        data.blockClassificationTitle,
        0,
        CLASS_Y,
        LEFT_W
    );

    addSectionHeader(
        "Корневые причины",
        LEFT_W,
        CLASS_Y,
        RIGHT_W
    );

    // =========================================================
    // КЛАССИФИКАЦИЯ
    // =========================================================

    const CLASS_INFO_Y = CLASS_Y + SECTION_H;

    const drawSimpleClassification = (
        startY: number
    ): number => {

        const rows = data.blockClassificationRows;

        rows.forEach((row, index) => {

            const y =
                startY +
                index * ROW_H;

            addCell(
                row.label,
                0,
                y,
                LABEL_W,
                ROW_H,
                {
                    fontSize: 8.5,
                    bold: true
                }
            );

            addCell(
                row.value,
                LABEL_W,
                y,
                LEFT_W - LABEL_W,
                ROW_H,
                {
                    fontSize: 8.5
                }
            );

        });

        return startY + rows.length * ROW_H;
    };

    const drawEquipmentClassification = (
        startY: number
    ): number => {

        const rows = data.blockClassificationRows;

        const topRows = rows.top;
        const equipmentRows = rows.bottom;

        //--------------------------------------------------------
        // Верхние строки
        //--------------------------------------------------------

        topRows.forEach((row, index) => {

            const y =
                startY +
                index * ROW_H;

            addCell(
                row.label,
                0,
                y,
                LABEL_W,
                ROW_H,
                {
                    bold: true,
                    fontSize: 8.5
                }
            );

            addCell(
                row.value,
                LABEL_W,
                y,
                LEFT_W - LABEL_W,
                ROW_H,
                {
                    fontSize: 8.5
                }
            );

        });

        //--------------------------------------------------------

        const pairY =
            startY +
            topRows.length * ROW_H;

        const halfWidth = LEFT_W / 2;

        const leftLabelWidth = LABEL_W * 0.60;
        const rightLabelWidth = LABEL_W * 0.60;

        const leftValueWidth = halfWidth - leftLabelWidth;
        const rightValueWidth = halfWidth - rightLabelWidth;

        equipmentRows.forEach((row, index) => {

            const y = pairY + index * ROW_H;

            // Левая колонка

            addCell(
                row.leftLabel,
                0,
                y,
                leftLabelWidth,
                ROW_H,
                {
                    bold: true,
                    fontSize: 8.5,
                }
            );

            addCell(
                row.leftValue,
                leftLabelWidth,
                y,
                leftValueWidth,
                ROW_H,
                {
                    fontSize: 8.5,
                }
            );

            // Правая колонка

            addCell(
                row.rightLabel,
                halfWidth,
                y,
                rightLabelWidth,
                ROW_H,
                {
                    bold: true,
                    fontSize: 8.5,
                }
            );

            addCell(
                row.rightValue,
                halfWidth + rightLabelWidth,
                y,
                rightValueWidth,
                ROW_H,
                {
                    fontSize: 8.5,
                }
            );

        });

        return pairY + equipmentRows.length * ROW_H;
    };

    let classificationBottom = CLASS_INFO_Y;

    switch (data.blockClassificationType) {
        case "equipment":
            classificationBottom =
                drawEquipmentClassification(
                    CLASS_INFO_Y
                );
            break;

        default:
            classificationBottom =
                drawSimpleClassification(
                    CLASS_INFO_Y
                );
    }

    // =========================================================
    // КОРНЕВЫЕ ПРИЧИНЫ
    // =========================================================

    const ROOT_X = LEFT_W;

    const ROOT_Y =
        CLASS_Y +
        SECTION_H;

    const ROOT_NUM_W = 0.28;

    const ROOT_CATEGORY_W = 0.76;

    const ROOT_DESCRIPTION_W =
        RIGHT_W -
        ROOT_NUM_W -
        ROOT_CATEGORY_W;

    const ROOT_NUM_H = 0.30;

    // Заголовок таблицы
    addCell(
        "№",
        ROOT_X,
        ROOT_Y,
        ROOT_NUM_W,
        ROOT_NUM_H,
        {
            fontSize: 8,
            bold: true,
            align: "center",
        }
    );

    addCell(
        "Описание",
        ROOT_X +
        ROOT_NUM_W,
        ROOT_Y,
        ROOT_DESCRIPTION_W,
        ROOT_NUM_H,
        {
            fontSize: 8,
            bold: true,
            align: "center",
        }
    );

    addCell(
        "Категория\nИшикавы",
        ROOT_X +
        ROOT_NUM_W +
        ROOT_DESCRIPTION_W,
        ROOT_Y,
        ROOT_CATEGORY_W,
        ROOT_NUM_H,
        {
            fontSize: 7.5,
            bold: true,
            align: "center",
        }
    );

    // =========================================================
    // ДИНАМИЧЕСКАЯ ВЫСОТА КОРНЕВЫХ ПРИЧИН
    // =========================================================

    const visibleRootCauses =
        data.rootCauses.slice(0, 3);

    const rootRowHeights =
        visibleRootCauses.map((item) =>
            getRowHeightByText(
                item.description,
                ROOT_DESCRIPTION_W
            )
        );

    let currentRootY =
        ROOT_Y +
        ROOT_NUM_H;

    visibleRootCauses.forEach(
        (rootCause, index) => {
            const rowHeight =
                rootRowHeights[index];

            addCell(
                String(index + 1),
                ROOT_X,
                currentRootY,
                ROOT_NUM_W,
                rowHeight,
                {
                    fontSize: 8,
                    align: "center",
                }
            );

            addCell(
                rootCause.description,
                ROOT_X +
                ROOT_NUM_W,
                currentRootY,
                ROOT_DESCRIPTION_W,
                rowHeight,
                {
                    fontSize: 8,
                    valign: "mid",
                }
            );

            addCell(
                rootCause.ishikawaCategory,
                ROOT_X +
                ROOT_NUM_W +
                ROOT_DESCRIPTION_W,
                currentRootY,
                ROOT_CATEGORY_W,
                rowHeight,
                {
                    fontSize: 8,
                    valign: "mid",
                    align: "center",
                }
            );

            currentRootY +=
                rowHeight;
        }
    );

    // =========================================================
    // ФИКСИРУЕМ НИЖНИЙ БЛОК
    //
    // Важно:
    // Фото/Действия начинаются сразу после
    // классификации без зазора.
    //
    // Но если rootCauses стали выше,
    // учитываем и их высоту.
    // =========================================================

    const rootTableHeight =
        ROOT_NUM_H +
        rootRowHeights.reduce(
            (sum, h) => sum + h,
            0
        );

    const rootCausesBottom =
        ROOT_Y +
        rootTableHeight;

    const BOTTOM_Y = Math.max(
        classificationBottom,
        rootCausesBottom
    );

    // =========================================================
    // ФОТО / ДЕЙСТВИЯ
    // =========================================================

    addSectionHeader(
        "Фото",
        0,
        BOTTOM_Y,
        LEFT_W
    );

    addSectionHeader(
        "Действия",
        LEFT_W,
        BOTTOM_Y,
        RIGHT_W
    );

    // =========================================================
    // ФОТО
    // =========================================================

    const PHOTO_Y =
        BOTTOM_Y +
        SECTION_H;

    const PHOTO_H =
        SLIDE_H -
        PHOTO_Y;

    if (
        data.photos &&
        data.photos.length
    ) {
        const photos =
            data.photos.slice(0, 4);

        const GAP = 0.06;

        if (
            photos.length === 1
        ) {
            slide.addImage({
                path: photos[0].path,

                x: GAP,
                y: PHOTO_Y + GAP,

                w:
                    LEFT_W -
                    GAP * 2,

                h:
                    PHOTO_H -
                    GAP * 2,

                sizingContain: true,
            });
        }

        if (
            photos.length === 2
        ) {
            photos.forEach(
                (photo, index) => {
                    slide.addImage({
                        path: photo.path,

                        x:
                            index *
                            (LEFT_W / 2) +
                            GAP,

                        y:
                            PHOTO_Y +
                            GAP,

                        w:
                            LEFT_W / 2 -
                            GAP * 2,

                        h:
                            PHOTO_H -
                            GAP * 2,

                        sizingContain: true,
                    });
                }
            );
        }

        if (
            photos.length >= 3
        ) {
            photos.forEach(
                (photo, index) => {
                    const col =
                        index % 2;

                    const row =
                        Math.floor(
                            index / 2
                        );

                    slide.addImage({
                        path: photo.path,

                        x:
                            col *
                            (LEFT_W / 2) +
                            GAP,

                        y:
                            PHOTO_Y +
                            row *
                            (PHOTO_H / 2) +
                            GAP,

                        w:
                            LEFT_W / 2 -
                            GAP * 2,

                        h:
                            PHOTO_H / 2 -
                            GAP * 2,

                        sizingContain: true,
                    });
                }
            );
        }
    } else {
        // Пустая область Фото,
        // если фотографии не переданы
        addRect(
            0,
            PHOTO_Y,
            LEFT_W,
            PHOTO_H,
            COLORS.white,
            COLORS.border,
            0.6
        );
    }

    // =========================================================
    // ДЕЙСТВИЯ
    // =========================================================

    const ACTION_X =
        LEFT_W;

    const ACTION_Y =
        BOTTOM_Y +
        SECTION_H;

    const ACTION_NUM_W =
        0.28;

    const ACTION_DEADLINE_W =
        0.75;

    const ACTION_DESCRIPTION_W =
        RIGHT_W -
        ACTION_NUM_W -
        ACTION_DEADLINE_W;

    const ACTION_HEADER_H =
        0.30;

    addCell(
        "№",
        ACTION_X,
        ACTION_Y,
        ACTION_NUM_W,
        ACTION_HEADER_H,
        {
            fontSize: 8,
            bold: true,
            align: "center",
        }
    );

    addCell(
        "Описание",
        ACTION_X +
        ACTION_NUM_W,
        ACTION_Y,
        ACTION_DESCRIPTION_W,
        ACTION_HEADER_H,
        {
            fontSize: 8,
            bold: true,
            align: "center",
        }
    );

    addCell(
        "Срок",
        ACTION_X +
        ACTION_NUM_W +
        ACTION_DESCRIPTION_W,
        ACTION_Y,
        ACTION_DEADLINE_W,
        ACTION_HEADER_H,
        {
            fontSize: 8,
            bold: true,
            align: "center",
        }
    );

    // =========================================================
    // ДЕЙСТВИЯ
    // =========================================================

    const visibleActions = data.actions; //data.actions.slice(0, 4);

    const actionRowHeights =
        visibleActions.map((item) =>
            getRowHeightByText(
                item.description,
                ACTION_DESCRIPTION_W
            )
        );

    let currentActionY =
        ACTION_Y +
        ACTION_HEADER_H;

    visibleActions.forEach(
        (action, index) => {
            const rowHeight =
                actionRowHeights[index];

            // №
            addCell(
                String(index + 1),
                ACTION_X,
                currentActionY,
                ACTION_NUM_W,
                rowHeight,
                {
                    fontSize: 8,
                    align: "center",
                    valign: "mid",
                }
            );

            // Описание
            addCell(
                action.description,
                ACTION_X +
                ACTION_NUM_W,
                currentActionY,
                ACTION_DESCRIPTION_W,
                rowHeight,
                {
                    fontSize: 8,
                    align: "left",
                    valign: "mid",
                }
            );

            // Срок
            addCell(
                action.deadline,
                ACTION_X +
                ACTION_NUM_W +
                ACTION_DESCRIPTION_W,
                currentActionY,
                ACTION_DEADLINE_W,
                rowHeight,
                {
                    fontSize: 8,
                    align: "center",
                    valign: "mid",
                }
            );
            currentActionY += rowHeight;
        }
    );

    // =========================================================
    // ВОЗВРАЩАЕМ PPTX
    // =========================================================

    return pptx;
};